import { useNavigate, useParams } from 'react-router-dom';
import BuildIcon from '@mui/icons-material/Build';
import { AppBar, Toolbar, Avatar, IconButton, Box, Typography, Icon, Tooltip, Chip } from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';
import ProjectsDropdown from './ProjectsDropdown';
import { useProject } from '../api/project';
import { useAssetCounts } from '../api/assets';
import { useReportModal } from '../contexts/ReportContext';
import { useAssetTypes } from '../api/assetTypes';
import { useState, useEffect, useRef } from 'react';
import { getAssetTypeIcon } from '../utils/assetIcons';
import SnapshotCaptureButton from './SnapshotCaptureButton';
import { formatArea } from '../utils/units';
import UserMenu from "./UserMenu.tsx";
import { useProjectConfig } from '../contexts/ProjectConfigContext';
import { useSimulation } from '../contexts/SimulationContext';
import { useSimulations, useSimulationIsCurrent } from '../api/simulations';
import MovieIcon from '@mui/icons-material/Movie';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import TenantLogoBadge from './TenantLogoBadge';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { switchHome } from '../api/tenants';

export default function Topbar() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useProject();
  const projIdForReport = project?.data?.id?.toString() || undefined;
  const { openModal } = useReportModal();
  const { openConfigModal } = useProjectConfig();
  const handleGenerateReport = () => {
    if (!projIdForReport) return;
    openModal(projIdForReport, null);
  }
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const { currentSavedSimId } = useSimulation();
  const { data: simulationsData } = useSimulations();
  const selectedSimulation = currentSavedSimId
    ? (simulationsData?.data || []).find((s: any) => s.simulation_id === currentSavedSimId)
    : null;
  const selectedSimulationName = selectedSimulation?.name || (currentSavedSimId ? 'Unnamed Simulation' : null);
  const { data: isCurrentData } = useSimulationIsCurrent(currentSavedSimId);
  const simIsCurrent = isCurrentData?.data?.is_current ?? null;
  // Get asset counts (without geometries)
  const { data: assetCountsData } = useAssetCounts(projectId || '');
  const { assetTypes } = useAssetTypes();
  const snapshotTargetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    snapshotTargetRef.current = document.querySelector('.map-container');
  }, [projectId]);

  const [assetTypeCounts, setAssetTypeCounts] = useState<any[]>([]);

  useEffect(() => {
    if (!assetCountsData?.data?.by_type?.length) {
      setAssetTypeCounts([]);
      return;
    }

    const typeCounts = new Map<string, number>();
    assetCountsData.data.by_type.forEach((item: { asset_type: string; count: number }) => {
      typeCounts.set(item.asset_type, item.count);
    });

    const newCounts = Array.from(typeCounts.entries())
      .map(([type, count]) => {
        const typeInfo = assetTypes?.data?.find((t: any) => t.value === type);
        return {
          type,
          count,
          label: typeInfo?.label || type,
        };
      })
      .filter((item) => item.count > 0);

    setAssetTypeCounts(newCounts);
  }, [assetCountsData, assetTypes]);

  // No longer needed since we're using a modal instead of separate pages
  // const isOnConfigPage = location.pathname.includes('/config');

  function handleHomeClick() {
    navigate('/projects');
  }

  function handleProjectConfigClick() {
    openConfigModal();
  }

  const projectArea = project?.data?.kpis?.find((kpi) => kpi.label === 'Total area');

  const unitSystem = user?.unit_system === 'imperial' ? 'imperial' : 'metric';
  const formattedProjectArea = (() => {
    if (projectArea?.value === undefined || projectArea?.value === null) {
      return 'N/A';
    }
    const hectares = Number(projectArea.value);
    if (!Number.isFinite(hectares)) {
      return 'N/A';
    }
    return formatArea(hectares, unitSystem);
  })();

  return (
    <AppBar position="fixed"
            sx={{
                  width: '100%',
                  top: 0,
                  left: 0,
                  height: 50,
                  backgroundColor: '#212b37',
                  color:'white',
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            elevation={1}>
      <Toolbar
          sx={{
            display: 'flex',
            "&.MuiToolbar-root": {
              minHeight: 50,
            }
          }}
      >
        <Box
          sx={{ width: '75%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
           }}>
          <Avatar
            src="/logo.png"
            sx={{
              height: 30,
              width: 'auto',
              cursor: 'pointer',
              mr: user?.tenant_logo_url ? 2 : 3 // Add right margin for spacing
            }}
            onClick={handleHomeClick}
            variant='square'
          />
          {user?.is_switched && (
            <Tooltip title={`You're working in ${user.tenant_name} from your ${user.home_user?.tenant_name ?? 'own'} account`} arrow>
              <Chip
                size="small"
                label={`Working in ${user.tenant_name}`}
                onDelete={() => {
                  switchHome().catch(() => undefined);
                }}
                deleteIcon={
                  <Tooltip title={`Back to ${user.home_user?.tenant_name ?? 'your organisation'}`} arrow>
                    <KeyboardReturnIcon />
                  </Tooltip>
                }
                sx={{
                  mr: 2,
                  bgcolor: 'rgba(0, 212, 173, 0.18)',
                  color: 'white',
                  border: '1px solid rgba(0, 212, 173, 0.6)',
                  '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)' },
                }}
              />
            </Tooltip>
          )}
          {user?.tenant_logo_url && (
            <>
              <Box sx={{ width: '1px', height: 26, bgcolor: 'rgba(255, 255, 255, 0.22)', mr: 2 }} />
              <Box sx={{ mr: 3, display: 'flex', alignItems: 'center' }}>
                <TenantLogoBadge
                  src={user.tenant_logo_url}
                  backdrop={user.tenant_logo_backdrop}
                  scale={user.tenant_logo_scale}
                />
              </Box>
            </>
          )}
          {project &&
            <>
              <ProjectsDropdown />
              <Tooltip title="Generate report" arrow>
                <span>
                  <IconButton
                    sx={{ ml: 1 }}
                    onClick={handleGenerateReport}
                    disabled={!projIdForReport}
                  >
                    <ExitToAppIcon sx={{ color: 'white' }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Story maps" arrow>
                <span>
                  <IconButton
                    onClick={() => navigate(`/projects/${projectId}/story-maps`)}
                    disabled={!projectId}
                  >
                    <AutoStoriesIcon sx={{ color: 'white' }} />
                  </IconButton>
                </span>
              </Tooltip>
              {!viewOnly && (
                <SnapshotCaptureButton
                  variant="icon"
                  tooltip="Capture snapshot of current map"
                  iconColor="white"
                  targetRef={snapshotTargetRef}
                />
              )}
              {selectedSimulationName && (
                <Tooltip title={`Selected simulation: ${selectedSimulationName}`} arrow>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      borderRadius: 1,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      px: 1,
                      py: 0.5,
                      ml: 2,
                      maxWidth: 260,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                    <MovieIcon sx={{ color: 'white', fontSize: 18, mr: 0.75 }} />
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: 'white',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selectedSimulationName}
                    </Typography>
                    {simIsCurrent !== null && (
                      <Chip
                        label={simIsCurrent ? 'Current' : 'Modified'}
                        size="small"
                        sx={{
                          ml: 1,
                          fontSize: '0.7rem',
                          fontWeight: simIsCurrent ? 500 : 400,
                          height: 20,
                          backgroundColor: simIsCurrent ? 'rgba(0, 212, 173, 0.25)' : 'rgba(244, 67, 54, 0.25)',
                          color: simIsCurrent ? '#00d4ad' : '#ef5350',
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              )}
              <Box sx={{ flexGrow: 1,
                          display: 'flex',
                          justifyContent: 'end',
                          alignItems: 'center'}}>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Project Size:
                  </Typography>
                  <Tooltip
                    title={`Project Area: ${formattedProjectArea}`}
                    arrow
                  >
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      borderRadius: 1,
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                      px: 1,
                      py: 0.5,
                      mr: 10,
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.2)'
                      }
                    }}>
                      <Icon sx={{alignSelf: 'center'}}>
                        aspect_ratio
                      </Icon>
                      <Typography
                        sx={{ fontWeight: 'bold', alignSelf: 'center', ml: 0.5 }}
                      >
                        {formattedProjectArea}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
                {/* Asset Type Stats */}
                <Box sx={{ display: 'flex', justifyContent: 'end', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Assets:
                  </Typography>
                  {assetTypeCounts.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Icon sx={{alignSelf: 'center'}}>
                        info
                      </Icon>
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        No assets
                      </Typography>
                    </Box>
                  ) : (
                    assetTypeCounts.map((assetTypeStat) => (
                      <Tooltip
                        key={`asset-type-${assetTypeStat.type}`}
                        title={`${assetTypeStat.count} ${assetTypeStat.label}${assetTypeStat.count !== 1 ? 's' : ''}`}
                        arrow
                      >
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          borderRadius: 1,
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          px: 1,
                          py: 0.5,
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.2)'
                          }
                        }}>
                          <Icon sx={{alignSelf: 'center'}}>
                            {getAssetTypeIcon(assetTypeStat.type)}
                          </Icon>
                          <Typography
                            sx={{ fontWeight: 'bold', alignSelf: 'center', ml: 0.5 }}
                          >
                            {assetTypeStat.count}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))
                  )}
                </Box>
              </Box>
            </>
          }
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', flexGrow: 1 }}>
          {project && !viewOnly && (
            <>
              <IconButton onClick={handleProjectConfigClick} sx={{ mr: 1 }} title="Project Settings">
                <BuildIcon sx={{ color: 'white' }} />
              </IconButton>
            </>
          )}
          <UserMenu/>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
